import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { WaterfallStructure } from 'further-types/lp/carry-settings';
import React from 'react';
import useWaterfallElements from '../hooks/useWaterfallElements';
import { numberToCurrencyString, renderMultiple } from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';

const remainingToDistributeTooltip =
  'This is the total distributable amount minus any funds distributed to date. Where funds have been over or under-allocated to the carried interest partner, the limited partner amount will be adjusted to account for this.';

type Row = {
  id: string;
  investorLabel: string;
  totalCommitment: string;
  paidInCapital: string;
  grossProceeds: string;
  [key: string]: string;
};

const columns: Array<TableColumn<Row>> = [
  { key: 'investorLabel', label: 'Investor', width: 200 },
  { key: 'totalCommitment', label: 'Total commitment', width: 200 },
  { key: 'paidInCapital', label: 'Paid in capital', width: 200 },
  { key: 'grossProceeds', label: 'Gross proceeds', width: 200 },
];

const splitColumns: Array<TableColumn<Row>> = [
  {
    key: 'cpProfitsSplit',
    label: 'Extra proceeds split',
    width: 200,
    withVerticalBorder: true,
    tooltip:
      "This step is the amount of excess proceeds allocated to the carry partner based on the vehicle's carried interest rate.",
  },
  { key: 'cpTotalDistributable', label: 'Total distributable', width: 200 },
  { key: 'cpDistributedToDate', label: 'Distributed to date', width: 200 },
  {
    key: 'cpRemainingToDistribute',
    label: 'Remaining to distribute',
    width: 200,
    tooltip: remainingToDistributeTooltip,
  },
  {
    key: 'lpProfitsSplit',
    label: 'Extra proceeds split',
    width: 200,
    withVerticalBorder: true,
    tooltip:
      "This step is the amount of excess proceeds allocated to the limited partners based on the vehicle's carried interest rate.",
  },
  { key: 'lpTotalDistributable', label: 'Total distributable', width: 200 },
  { key: 'lpDpi', label: 'DPI', width: 200 },
  { key: 'lpDistributedToDate', label: 'Distributed to date', width: 200 },
  {
    key: 'lpRemainingToDistribute',
    label: 'Remaining to distribute',
    width: 200,
    tooltip: remainingToDistributeTooltip,
  },
];

type Props = {
  baseCurrency: CurrencyCode;
  fundsToDistribute: Array<{
    commitmentId: string;
    investorLabel: string;
    waterfall: {
      totalCommitment: number;
      paidInCapital: number;
      grossProceeds: number;
      preferredReturnAccrued: {
        accruedPreferredReturn: number;
      };
      catchUpAccrued: number;
      waterfallElements: Array<{
        element: WaterfallStructure;
        value: number;
      }>;
      capitalSplit: {
        carryPartner: {
          excessProceeds: number;
          remainingToDistribute: number;
          totalDistributable: number;
          distributedToDate: number;
        };
        limitedPartner: {
          excessProceeds: number;
          remainingToDistribute: number;
          totalDistributable: number;
          distributedToDate: number;
          dpi: number;
        };
      };
    };
  }>;
};

const WaterfallCalculations: React.FC<Props> = ({
  fundsToDistribute,
  baseCurrency,
}) => {
  const waterfallSteps = useWaterfallElements(fundsToDistribute, baseCurrency);

  const renderValue = (value: number) =>
    numberToCurrencyString(value, { currency: baseCurrency });

  const rows: Array<Row> = fundsToDistribute.map((commitmentDistribution) => ({
    id: commitmentDistribution.commitmentId,
    investorLabel: commitmentDistribution.investorLabel,
    totalCommitment: renderValue(
      commitmentDistribution.waterfall.totalCommitment,
    ),
    paidInCapital: renderValue(commitmentDistribution.waterfall.paidInCapital),
    grossProceeds: renderValue(commitmentDistribution.waterfall.grossProceeds),
    preferredReturnAccrued: renderValue(
      commitmentDistribution.waterfall.preferredReturnAccrued
        .accruedPreferredReturn,
    ),
    catchUpAccrued: renderValue(
      commitmentDistribution.waterfall.catchUpAccrued,
    ),
    ...waterfallSteps.getValues(commitmentDistribution.commitmentId),
    cpProfitsSplit: renderValue(
      commitmentDistribution.waterfall.capitalSplit.carryPartner.excessProceeds,
    ),
    cpTotalDistributable: renderValue(
      commitmentDistribution.waterfall.capitalSplit.carryPartner
        .totalDistributable,
    ),
    cpDistributedToDate: renderValue(
      commitmentDistribution.waterfall.capitalSplit.carryPartner
        .distributedToDate,
    ),
    cpRemainingToDistribute: renderValue(
      commitmentDistribution.waterfall.capitalSplit.carryPartner
        .remainingToDistribute,
    ),
    lpProfitsSplit: renderValue(
      commitmentDistribution.waterfall.capitalSplit.limitedPartner
        .excessProceeds,
    ),
    lpTotalDistributable: renderValue(
      commitmentDistribution.waterfall.capitalSplit.limitedPartner
        .totalDistributable,
    ),
    lpDpi: renderMultiple(
      commitmentDistribution.waterfall.capitalSplit.limitedPartner.dpi,
    ),
    lpDistributedToDate: renderValue(
      commitmentDistribution.waterfall.capitalSplit.limitedPartner
        .distributedToDate,
    ),
    lpRemainingToDistribute: renderValue(
      commitmentDistribution.waterfall.capitalSplit.limitedPartner
        .remainingToDistribute,
    ),
  }));

  const waterfallStepColumns = waterfallSteps.getColumns();

  return (
    <Table
      columns={[...columns, ...waterfallStepColumns, ...splitColumns]}
      columnGroups={[
        {
          label: 'Waterfall calculation',
          colspan: columns.length + waterfallStepColumns.length + 1,
        },
        { label: 'Carry partner', colspan: 4 },
        { label: 'Limited partners', colspan: 5 },
      ]}
      rows={rows}
      disableSorting
      scrollable
      showTopBorder
      showHeaderBorder
    />
  );
};

export default WaterfallCalculations;
